<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js'
import { useEventQuery } from '@/enitites/event/api/queries'
import { useImage } from '@/shared/composables/image'
import { computed, ref, watch } from 'vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import { useCartMutation, useCheckoutMutation, useTicketsForEventQuery } from '@/features/tickets/api/queries'
import PurchaseTicketItem from '@/features/tickets/ui/PurchaseTicketItem.vue'
import OrderSummary from '@/features/tickets/ui/OrderSummary.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import useValidationRules from '@/shared/composables/validation'
import { useCart } from '@/features/tickets/composables/useCart'
import { usePricing } from '@/features/tickets/composables/usePricing'
import { useRouter } from 'vue-router/composables'

const props = defineProps<{
  eventId: number
}>()

const { getImageById } = useImage()
const rules = useValidationRules()
const getUserDB = computed(() => store.getters.getUserDB)
const { data: event, isLoading: isLoadingEvent, isError: isEventError } = useEventQuery(props.eventId)
const { data: tiers, isLoading: isLoadingTickets, isError: isTicketsError } = useTicketsForEventQuery(props.eventId)
const { mutate: postCart, isPending: isPostingCart, error: errorPostingCart, data: cartSummary } = useCartMutation()
const { mutate: postCheckout, isPending: isPostingCheckout, error: errorPostingCheckout } = useCheckoutMutation()

const STEPS = {
  CHOOSE_TICKETS: 'Choose tickets',
  CHECKOUT: 'Check out',
} as const

const currentStep = ref<keyof typeof STEPS>('CHOOSE_TICKETS')
const isFormValid = ref(true)
const discountCode = ref('')
const tickets = computed(() => tiers.value?.tiers || [])
const stripeError = ref<string | null>(null)
const isPaymentInitialized = ref(false)

// Use composables for cart and pricing logic
const { cart, updateCart, isCartEmpty, updateDiscountCode, updateContactInfo } = useCart()
const { getTicketById } = usePricing(tickets, cart)

const router = useRouter()

// Initialize cart with user data if available
if (getUserDB.value?.aEmail || getUserDB.value?.fullName) {
  updateContactInfo(getUserDB.value?.aEmail || getUserDB.value?.parentEmail || '', getUserDB.value?.fullName || '')
}

const isCheckoutDisabled = computed(() => isCartEmpty.value)

const initPaymentElement = async (clientSecret: string) => {
  isPaymentInitialized.value = true

  let stripe
  try {
    stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
  } catch (error) {
    stripeError.value = (error as Error)?.message || 'An error occurred while loading payment gateway.'
    isPaymentInitialized.value = false
    return
  }

  if (!stripe) {
    stripeError.value = 'Failed to load payment gateway.'
    return
  }

  let checkout
  try {
    stripeError.value = null
    checkout = await stripe.initEmbeddedCheckout({ clientSecret })
  } catch (error) {
    stripeError.value = (error as Error)?.message || 'An error occurred while initializing payment.'
    isPaymentInitialized.value = false
    return
  }

  if (!checkout) {
    stripeError.value = 'Failed to initialize payment elements.'
    return
  }

  checkout.mount('#payment-element')
}

const handlePurchase = () => {
  if (!isFormValid.value || isPostingCart.value || !cartSummary.value || isPostingCheckout.value) return

  postCheckout(
    {
      eventId: props.eventId,
      data: {
        ...cartSummary.value,
        contactEmail: cart.value.contactEmail,
        contactName: cart.value.contactName,
      },
    },
    {
      onSuccess: (response) => {
        if ('clientSecret' in response && response.clientSecret) {
          try {
            stripeError.value = null
            initPaymentElement(response.clientSecret)
          } catch (error) {
            stripeError.value = (error as Error)?.message || 'An error occurred while initializing payment.'
          }
        } else if (event.value) {
          router.replace({ name: 'PurchaseComplete', params: { id: event.value.id.toString() } })
        }
      },
    }
  )
}

const handleApplyDiscountCode = () => {
  updateDiscountCode(discountCode.value)

  if (cart.value.tiers.length > 0) {
    postCart(
      {
        eventId: props.eventId,
        data: cart.value,
      },
      {
        onSuccess: (response) => {
          discountCode.value = response.discountCode || ''
        },
      }
    )
  }
}

watch(discountCode, (code) => {
  updateDiscountCode(code)
})

watch(
  () => cart.value.tiers,
  (newTiers) => {
    if (newTiers.length > 0) {
      postCart({
        eventId: props.eventId,
        data: {
          tiers: newTiers,
          contactEmail: cart.value.contactEmail,
          contactName: cart.value.contactName,
          discountCode: cart.value.discountCode,
        },
      })
    }
  },
  { deep: true }
)
</script>

<template>
  <div class="purchase-interface-container">
    <div
      class="purchase-ticket-interface"
      :class="{ 'payment-initialized': isPaymentInitialized }"
    >
      <div class="header">
        <template v-if="isLoadingEvent">
          <v-skeleton-loader
            type="image"
            height="84"
            width="84"
          />
          <div class="skeleton-info">
            <v-skeleton-loader type="heading" />
            <v-skeleton-loader
              type="text@2"
              width="150"
            />
          </div>
        </template>

        <template v-else-if="isEventError">
          <p class="error-message">Error loading event data</p>
        </template>
        <template v-else-if="event">
          <v-img
            :src="getImageById(event.image, 168, 168, 'event')"
            height="84"
            width="84"
            class="event-image"
          />
          <div class="event-info">
            <h2 class="event-title">{{ event.title }}</h2>
            <p class="event-info-row">{{ event.dateFlat }}</p>
            <p class="event-info-row">{{ event.timeFlat }}</p>
          </div>
        </template>
      </div>
      <div class="tickets">
        <div id="payment-element" />
        <template v-if="!isPaymentInitialized">
          <v-tabs
            v-model="currentStep"
            background-color="transparent"
            color="var(--color-dark-800)"
            class="tabs"
            grow
          >
            <v-tab tab-value="CHOOSE_TICKETS">
              {{ STEPS.CHOOSE_TICKETS }}
            </v-tab>
            <v-tab
              tab-value="CHECKOUT"
              :disabled="isCheckoutDisabled"
            >
              {{ STEPS.CHECKOUT }}
            </v-tab>
          </v-tabs>
          <v-tabs-items
            v-model="currentStep"
            class="tabs-items"
            touchless
          >
            <v-tab-item
              value="CHOOSE_TICKETS"
              class="tickets-list"
            >
              <template v-if="isLoadingTickets">
                <p>Loading tickets...</p>
              </template>
              <template v-else-if="isTicketsError">
                <p class="error-message">Error loading tickets.</p>
              </template>
              <template v-else-if="tickets && tickets.length">
                <PurchaseTicketItem
                  v-for="ticket in tickets"
                  :key="ticket.id"
                  :ticket="ticket"
                  @update:quantity="updateCart"
                />
              </template>
              <template v-else>
                <p>No tickets available for this event.</p>
              </template>
            </v-tab-item>
            <v-tab-item value="CHECKOUT">
              <div class="checkout-form">
                <v-form
                  v-model="isFormValid"
                  class="form-section"
                >
                  <p class="section-title">Contact Information</p>
                  <BKInput
                    v-model="cart.contactName"
                    label="Full name"
                    placeholder="Enter your full name"
                    :rules="[rules.required]"
                  />
                  <BKInput
                    v-model="cart.contactEmail"
                    label="Email address"
                    placeholder="Enter your email address"
                    :rules="[rules.required, rules.email]"
                  />
                </v-form>
                <div class="form-section">
                  <p class="section-title">Discount Code</p>
                  <div class="discount-code-input-wrapper">
                    <BKInput
                      v-model="discountCode"
                      placeholder="Enter discount code"
                      :readonly="!!cartSummary?.discountCode && !cartSummary?.discountError"
                      :error-messages="cartSummary?.discountError ? [cartSummary.discountError] : []"
                    />
                    <BKButton
                      v-if="!cartSummary?.discountCode || cartSummary?.discountError"
                      outlined
                      width="auto"
                      height="56px"
                      @click="handleApplyDiscountCode"
                    >
                      Apply
                    </BKButton>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </template>
      </div>
      <div
        v-if="!isPaymentInitialized"
        class="actions"
      >
        <BKButton
          v-if="currentStep === 'CHOOSE_TICKETS'"
          white-text
          :disabled="isCheckoutDisabled"
          @click="currentStep = 'CHECKOUT'"
        >
          Proceed to Checkout
        </BKButton>
        <template v-if="currentStep === 'CHECKOUT'">
          <BKButton
            outlined
            width="auto"
            @click="currentStep = 'CHOOSE_TICKETS'"
          >
            Go Back
          </BKButton>
          <BKButton
            white-text
            width="auto"
            color="var(--color-green)"
            :disabled="!isFormValid"
            :loading="isPostingCart || isPostingCheckout"
            @click="handlePurchase"
          >
            Purchase
          </BKButton>
        </template>
      </div>
      <div
        v-if="errorPostingCart || errorPostingCheckout"
        class="error-message"
      >
        {{ errorPostingCart?.message || errorPostingCheckout?.message }}
      </div>
      <OrderSummary
        v-if="!isPaymentInitialized"
        class="summary-area"
        :cart-summary="isCartEmpty ? undefined : cartSummary"
        :get-ticket-by-id="getTicketById"
        :is-loading="isPostingCart"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.purchase-interface-container {
  container-type: inline-size;
}

.purchase-ticket-interface {
  display: grid;
  grid-template-areas:
    'header  summary'
    'tickets summary'
    'actions summary'
    'error   summary';
  grid-template-columns: 1fr 304px;
  grid-template-rows: auto 1fr auto;
  max-height: 90vh;
  overflow: hidden;

  &.payment-initialized {
    grid-template-areas:
      'header'
      'tickets';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    overflow-y: auto;
    max-height: unset;
  }

  p {
    margin: 0;
  }

  @container (max-width: 800px) {
    grid-template-areas:
      'header'
      'tickets'
      'summary'
      'actions';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    overflow-y: auto;
    max-height: unset;
  }

  @container (max-width: 600px) {
    padding-top: 50px;
  }
}

.header {
  position: relative;
  grid-area: header;
  display: flex;
  gap: 16px;
  padding: 40px;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100%;
    height: 3px;
    background-image: linear-gradient(to bottom, rgb(0 0 0 / 10%), transparent);
  }

  .skeleton-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    gap: 8px;
  }

  .event-image {
    flex: 0 0 auto;
    border-radius: 8px;
  }

  .event-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;

    .event-title {
      font-weight: var(--font-weight-semi-bold);
      font-size: 24px;
      line-height: 30px;
      color: var(--color-dark-800);
    }

    .event-info-row {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-grey-300);
    }
  }

  @container (max-width: 600px) {
    padding: 24px 12px;
  }
}

.tickets {
  grid-area: tickets;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 20px;
  padding: 30px 40px;
  overflow-y: auto;

  @container (max-width: 600px) {
    padding: 16px 12px;
  }

  .v-tab {
    text-transform: unset;

    &.v-tab--active {
      font-weight: 700;
    }
  }

  .v-window {
    overflow: visible;
  }

  .tickets-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.checkout-form {
  display: flex;
  flex-direction: column;
  gap: 40px;

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .section-title {
      margin-bottom: 4px;
      font-size: 24px;
      line-height: 30px;
      font-weight: 700;
      color: var(--color-dark-800);
    }

    .discount-code-input-wrapper {
      display: flex;
      gap: 10px;
    }
  }
}

.actions {
  grid-area: actions;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding: 30px 40px 40px;
  border-top: 1px solid var(--color-grey-700);

  > * {
    flex: 1 1 0;
    min-width: fit-content;
  }
}

.summary-area {
  grid-area: summary;
}

.error-message {
  grid-area: error;
  margin: 0 40px 20px;
  font-size: 16px;
  line-height: 24px;
  color: red;
}

#payment-element:empty {
  display: none;
}
</style>
